import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Button, Form, InputGroup, Spinner, ButtonGroup } from 'react-bootstrap';
import { CalendarRange,Calendar, CalendarDays, List, Grid, Search, Filter, Plus, Pencil, Trash2, FileUp, Home, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reservation, House } from '../../../services/types';
import reservationsService from '../../../services/reservationsService';
import housesService from '../../../services/housesService';
import { showSuccess, showError, showConfirm } from '../../../utils/notifications';
import { formatDate } from '../../../utils/reservationHelpers';
import { exportReservationsToExcel } from '../../../utils/excelExport';
import ReservationDetailModal from '../../../components/reservations/ReservationDetailModal';
import CreateReservationModal from '../../../components/reservations/CreateReservationModal';
import MultiMonthCalendarView from '../../../components/reservations/MultiMonthCalendarView';
import ListView from '../../../components/reservations/ListView';
import CalendarView from "../../../components/reservations/CalendarView";
import HouseCalendarView from '../../../components/reservations/HouseCalendarView';
import DataTable, { ColumnDef, ActionButton, StatusBadge } from '../../../components/common/DataTable';
import { useUserData } from '../../../hooks';


type ViewMode = 'houseCalendar' | 'multiCalendar' |'calendar' | 'table' | 'list';


// LocalStorage key prefix for view preference
const RESERVATION_VIEW_PREFERENCE_KEY_PREFIX = 'cms_reservations_view_preference_user_';

// Helper functions for localStorage with user-specific keys
const getStoredViewPreference = (userId: string | null): ViewMode | null => {
    if (!userId) return null;

    try {
        const key = `${RESERVATION_VIEW_PREFERENCE_KEY_PREFIX}${userId}`;
        const stored = localStorage.getItem(key);
        if (stored && ['houseCalendar', 'multiCalendar', 'calendar', 'table', 'list'].includes(stored)) {
            return stored as ViewMode;
        }
    } catch (error) {
        console.warn('Failed to load view preference from localStorage:', error);
    }
    return null;
};

const saveViewPreference = (view: ViewMode, userId: string | null): void => {
    if (!userId) return;

    try {
        const key = `${RESERVATION_VIEW_PREFERENCE_KEY_PREFIX}${userId}`;
        localStorage.setItem(key, view);
    } catch (error) {
        console.warn('Failed to save view preference to localStorage:', error);
    }
};

const Reservations = () => {
    const { canEdit, canDelete, userID, role } = useUserData();
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [houses, setHouses] = useState<House[]>([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    // Load saved view preference for this specific user or default to 'houseCalendar'
    const [viewMode, setViewModeState] = useState<ViewMode>(() => {
        const savedView = getStoredViewPreference(userID);
        return savedView || 'houseCalendar';
    });

    // Wrapper to save view preference when changing view (per user)
    const setViewMode = (view: ViewMode) => {
        setViewModeState(view);
        saveViewPreference(view, userID);
    };

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [houseFilter, setHouseFilter] = useState<string>('all');

    // Modal state
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [prefilledDates, setPrefilledDates] = useState<{ checkIn: Date; checkOut: Date } | undefined>();
    const [extractedData, setExtractedData] = useState<any>(null);

    // PDF upload state
    const [uploadingPDF, setUploadingPDF] = useState(false);
    const [deletingReservation, setDeletingReservation] = useState(false);
    const pdfInputRef = useRef<HTMLInputElement>(null);

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Only force away from table view on mobile (house calendar is now available on mobile)
            if (mobile && viewMode === 'table') {
                setViewMode('houseCalendar');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [viewMode]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [reservationsData, housesData] = await Promise.all([
                reservationsService.getAll(),
                housesService.getAll(),
            ]);

            console.log('Loaded reservations:', reservationsData?.length || 0);
            console.log('Loaded houses:', housesData?.length || 0);

            setReservations(Array.isArray(reservationsData) ? reservationsData : []);
            setHouses(Array.isArray(housesData) ? housesData : []);

            if (!housesData || housesData.length === 0) {
                console.warn('No houses loaded. House names will show as "Unknown"');
            }
        } catch (err: any) {
            console.error('Error loading data:', err);
            showError('Error!', err.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Filtered reservations
    const filteredReservations = useMemo(() => {
        return reservations.filter((reservation) => {
            // Search filter
            const matchesSearch = searchQuery === '' ||
                reservation.name_first_guess?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                reservation.contact.toLowerCase().includes(searchQuery.toLowerCase());

            // Status filter
            const matchesStatus = statusFilter === 'all' ||
                reservation.reservation_state === statusFilter;

            // House filter
            const matchesHouse = houseFilter === 'all' ||
                reservation.house_id === houseFilter;

            return matchesSearch && matchesStatus && matchesHouse;
        });
    }, [reservations, searchQuery, statusFilter, houseFilter]);

    const handleSelectReservation = (reservation: Reservation) => {
        setSelectedReservation(reservation);
        setShowDetailModal(true);
    };

    const handleEditFromTable = (reservation: Reservation) => {
        // Open edit modal with reservation data
        setSelectedReservation(reservation);
        setShowCreateModal(true);
    };

    const handleEditFromDetail = () => {
        if (!selectedReservation) return;
        // Close detail modal and open edit modal
        setShowDetailModal(false);
        setShowCreateModal(true);
    };

    const handleConfirm = async () => {
        if (!selectedReservation) return;
        try {
            const updated = await reservationsService.confirm(selectedReservation.public_id);
            setReservations(reservations.map(r =>
                r.public_id === updated.public_id ? updated : r
            ));
            showSuccess('Confirmed!', 'Reservation has been confirmed.');
            setShowDetailModal(false);
            setSelectedReservation(null);
            // Refresh data to ensure everything is up to date
            await fetchData();
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to confirm reservation');
        }
    };

    const handleCancel = async () => {
        if (!selectedReservation) return;
        const confirmed = await showConfirm(
            'Delete Reservation?',
            'Are you sure you want to delete this reservation? This action cannot be undone.'
        );
        if (!confirmed) return;

        setDeletingReservation(true);
        try {
            await reservationsService.cancel(selectedReservation.public_id);
            setReservations(reservations.filter(r => r.public_id !== selectedReservation.public_id));
            showSuccess('Deleted!', 'Reservation has been deleted successfully.');
            setShowDetailModal(false);
            setSelectedReservation(null);
            await fetchData();
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to delete reservation');
        } finally {
            setDeletingReservation(false);
        }
    };

    const handleSelectSlot = (slotInfo: { start: Date; end: Date; houseId?: string }) => {
        setSelectedReservation(null); // Clear any selected reservation (create mode)

        // If houseId is provided (from house calendar), pre-fill it
        const reservation: any = {
            check_in_date: slotInfo.start.toISOString(),
            check_out_date: slotInfo.end.toISOString(),
        };

        if (slotInfo.houseId) {
            reservation.house_id = slotInfo.houseId;
        }

        // For now, we'll use extractedData to pass the house pre-fill
        if (slotInfo.houseId) {
            setExtractedData(reservation);
        }

        setPrefilledDates({
            checkIn: slotInfo.start,
            checkOut: slotInfo.end,
        });
        setShowCreateModal(true);
    };

    const handleCreateReservation = async (reservationData: any, reservationId?: string) => {
        try {
            if (reservationId) {
                // Update existing reservation
                const updated = await reservationsService.update(reservationId, reservationData);
                setReservations(reservations.map(r =>
                    r.public_id === reservationId ? updated : r
                ));
                showSuccess('Updated!', 'Reservation has been updated successfully.');
            } else {
                // Create new reservation
                const created = await reservationsService.create(reservationData);
                setReservations([...reservations, created]);
                showSuccess('Created!', 'Reservation has been created successfully.');
            }
            // Clear extracted data after successful creation
            setExtractedData(null);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || '';

            // Check for date conflict error
            if (errorMessage.toLowerCase().includes('dates conflict') ||
                errorMessage.toLowerCase().includes('impossible to create reservation')) {
                showError(
                    'Date Conflict!',
                    'Cannot create reservation. You already have a reservation for these dates at this property. Please choose different dates.'
                );
            } else if (errorMessage.toLowerCase().includes('date') && errorMessage.toLowerCase().includes('past')) {
                showError(
                    'Invalid Dates!',
                    'Check-in and check-out dates cannot be in the past. Please select future dates.'
                );
            } else {
                showError(
                    'Error!',
                    errorMessage || `Failed to ${reservationId ? 'update' : 'create'} reservation`
                );
            }
            throw err;
        }
    };

    const handlePDFUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            showError('Invalid File', 'Please upload a PDF file.');
            return;
        }

        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            showError('File Too Large', 'PDF file must be less than 10MB.');
            return;
        }

        try {
            setUploadingPDF(true);
            const extractedData = await reservationsService.extractFromPDF(file);

            // Set extracted data and open modal
            setExtractedData(extractedData);
            setSelectedReservation(null);
            setPrefilledDates(undefined);
            setShowCreateModal(true);

            showSuccess('Success!', 'Reservation data extracted from PDF successfully.');
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to extract data from PDF. Please try again or enter manually.');
        } finally {
            setUploadingPDF(false);
            // Reset file input
            if (pdfInputRef.current) {
                pdfInputRef.current.value = '';
            }
        }
    };

    const handleUploadPDFClick = () => {
        pdfInputRef.current?.click();
    };

    const handleDownloadExcel = () => {
        try {
            exportReservationsToExcel(filteredReservations, houses);
            showSuccess('Success!', 'Reservations exported to Excel successfully.');
        } catch (err: any) {
            showError('Error!', 'Failed to export reservations to Excel. Please try again.');
            console.error('Error exporting to Excel:', err);
        }
    };

    const getHouseName = (houseId: string) => {
        if (!houseId) {
            console.warn('getHouseName called with empty houseId');
            return 'No House';
        }

        if (!houses || houses.length === 0) {
            console.warn('Houses array is empty');
            return 'Loading...';
        }

        const house = houses.find(h => h.public_id === houseId);

        if (!house) {
            console.warn(`House not found for ID: ${houseId}. Available houses:`, houses.map(h => h.public_id));
            return 'Unknown House';
        }

        return house.name || house.address || 'Unnamed House';
    };

    const handleDelete = async (reservation: Reservation) => {
        const confirmed = await showConfirm(
            'Delete Reservation?',
            'Are you sure you want to delete this reservation? This action cannot be undone.'
        );
        if (!confirmed) return;

        try {
            await reservationsService.cancel(reservation.public_id);
            setReservations(reservations.filter(r => r.public_id !== reservation.public_id));
            showSuccess('Deleted!', 'Reservation has been deleted successfully.');
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to delete reservation');
        }
    };

    const calculateNights = (checkIn: string, checkOut: string) => {
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    };

    const getStatusVariant = (state: string): 'success' | 'danger' | 'warning' => {
        switch (state) {
            case 'confirmed':
                return 'success';
            case 'cancelled':
                return 'danger';
            default:
                return 'warning';
        }
    };

    const renderTableView = () => {
        // Define table columns
        const columns: ColumnDef<Reservation>[] = [
            {
                key: 'reservation_state',
                header: 'Status',
                sortable: true,
                render: (row) => (
                    <StatusBadge
                        status={row.reservation_state === 'confirmed' ? 'Confirmed' : row.reservation_state === 'cancelled' ? 'Cancelled' : 'Pending'}
                        variant={getStatusVariant(row.reservation_state)}
                    />
                ),
            },
            {
                key: 'name_first_guess',
                header: 'Guest',
                sortable: true,
                render: (row) => (
                    <div style={{ fontWeight: '500', color: '#111827' }}>
                        {row.name_first_guess || 'Guest'}
                    </div>
                ),
            },
            {
                key: 'house_id',
                header: 'Property',
                sortable: true,
                render: (row) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {getHouseName(row.house_id)}
                    </div>
                ),
            },
            {
                key: 'check_in_date',
                header: 'Check-in',
                sortable: true,
                render: (row) => (
                    <div style={{ color: '#374151', fontSize: '0.875rem' }}>
                        {formatDate(row.check_in_date, 'MMM dd, yyyy')}
                    </div>
                ),
            },
            {
                key: 'check_out_date',
                header: 'Check-out',
                sortable: true,
                render: (row) => (
                    <div style={{ color: '#374151', fontSize: '0.875rem' }}>
                        {formatDate(row.check_out_date, 'MMM dd, yyyy')}
                    </div>
                ),
            },
            {
                key: 'nights',
                header: 'Nights',
                sortable: true,
                render: (row) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem', fontWeight: '500' }}>
                        {calculateNights(row.check_in_date, row.check_out_date)}
                    </div>
                ),
            },
            {
                key: 'contact',
                header: 'Contact',
                sortable: true,
                render: (row) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {row.contact}
                    </div>
                ),
            },
            {
                key: 'platform',
                header: 'Platform',
                sortable: true,
                render: (row) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {row.platform || '-'}
                    </div>
                ),
            },
        ];

        // Define table actions based on permissions
        const actions: ActionButton<Reservation>[] = [
            ...(canEdit ? [{
                label: 'Edit',
                icon: <Pencil size={14} />,
                variant: 'secondary' as const,
                onClick: handleEditFromTable,
            }] : []),
            ...(canDelete ? [{
                label: 'Delete',
                icon: <Trash2 size={14} />,
                variant: 'danger' as const,
                onClick: handleDelete,
                show: (row: Reservation) => row.reservation_state !== 'cancelled',
            }] : []),
        ];

        return (
            <DataTable
                columns={columns}
                data={filteredReservations}
                actions={actions}
                onRowClick={handleSelectReservation}
                emptyMessage="No reservations found"
                keyExtractor={(row) => row.public_id}
            />
        );
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading reservations...</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Header */}
            <div className={`d-flex ${isMobile ? 'flex-column gap-3' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && (
                    <div>
                        <h4 className="mb-1" style={{ fontSize: '1.5rem' }}>Reservations Management</h4>
                        <p className="text-muted small mb-0">
                            {filteredReservations.length} of {reservations.length} reservations
                        </p>
                    </div>
                )}
                {isMobile && (
                    <div className="text-muted small mb-0">
                        {filteredReservations.length} of {reservations.length} reservations
                    </div>
                )}
                <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
                    {/* Super Admin only: Download Excel button */}
                    {role === 'Super Admin' && (
                        <Button
                            variant="outline-success"
                            onClick={handleDownloadExcel}
                            className={isMobile ? 'flex-grow-1' : ''}
                            title="Export reservations to Excel"
                        >
                            <Download size={18} className="me-2" />
                            {isMobile ? 'Excel' : 'Download Excel'}
                        </Button>
                    )}

                    {canEdit && (
                        <>
                            <input
                                ref={pdfInputRef}
                                type="file"
                                accept="application/pdf"
                                onChange={handlePDFUpload}
                                style={{ display: 'none' }}
                            />
                            <Button
                                variant="outline-primary"
                                onClick={handleUploadPDFClick}
                                disabled={uploadingPDF}
                                className={isMobile ? 'flex-grow-1' : ''}
                                style={{
                                    opacity: uploadingPDF ? 0.6 : 1,
                                    cursor: uploadingPDF ? 'not-allowed' : 'pointer',
                                    backgroundColor: uploadingPDF ? '#e9ecef' : undefined,
                                    borderColor: uploadingPDF ? '#dee2e6' : undefined,
                                    color: uploadingPDF ? '#6c757d' : undefined
                                }}
                            >
                                {uploadingPDF ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <FileUp size={18} className="me-2" />
                                        {isMobile ? 'Upload PDF' : 'Upload PDF'}
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setSelectedReservation(null);
                                    setPrefilledDates(undefined);
                                    setExtractedData(null);
                                    setShowCreateModal(true);
                                }}
                                className={isMobile ? 'flex-grow-1' : ''}
                            >
                                <Plus size={18} className="me-2" />
                                {isMobile ? 'New' : 'New Reservation'}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Filters and View Toggle */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <div className="row g-3 align-items-end">
                        {/* Search */}
                        <div className="col-12 col-lg-4">
                            <Form.Label className="small text-muted mb-1">Search</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <Search size={16} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search by guest or contact..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </InputGroup>
                        </div>

                        {/* Status Filter */}
                        <div className="col-6 col-lg-3">
                            <Form.Label className="small text-muted mb-1">Status</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <Filter size={16} />
                                </InputGroup.Text>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="cancelled">Cancelled</option>
                                </Form.Select>
                            </InputGroup>
                        </div>

                        {/* House Filter */}
                        <div className="col-6 col-lg-3">
                            <Form.Label className="small text-muted mb-1">House</Form.Label>
                            <Form.Select
                                value={houseFilter}
                                onChange={(e) => setHouseFilter(e.target.value)}
                            >
                                <option value="all">All Houses</option>
                                {houses.map((house) => (
                                    <option key={house.public_id} value={house.public_id}>
                                        {house.name || house.address}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>

                        {/* View Toggle */}
                        <div className="col-12 col-lg-2">
                            <Form.Label className="small text-muted mb-1 d-none d-lg-block">&nbsp;</Form.Label>
                            <ButtonGroup className="w-100">
                                <Button
                                    variant={viewMode === 'houseCalendar' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('houseCalendar')}
                                    title="House Calendar View - By Property"
                                >
                                    <CalendarRange size={18} />
                                </Button>
                                <Button
                                    variant={viewMode === 'multiCalendar' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('multiCalendar')}
                                    title="Multi Calendar View"
                                >
                                    <CalendarDays size={18} />
                                </Button>
                                <Button
                                    variant={viewMode === 'calendar' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('calendar')}
                                    title="Calendar View"
                                >
                                    <Calendar size={18} />
                                </Button>

                                <Button
                                    variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('list')}
                                    title="List View"
                                >
                                    <List size={18} />
                                </Button>
                                {!isMobile && (
                                    <Button
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        onClick={() => setViewMode('table')}
                                        title="Table View"
                                    >
                                        <Grid size={18} />
                                    </Button>
                                )}
                            </ButtonGroup>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* View Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={viewMode}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                >

                    {viewMode === 'houseCalendar' && (
                        <HouseCalendarView
                            reservations={filteredReservations}
                            houses={houses}
                            onSelectReservation={handleSelectReservation}
                            onSelectSlot={canEdit ? (slotInfo) => handleSelectSlot(slotInfo) : undefined}
                        />
                    )}

                    {viewMode === 'multiCalendar' && (
                        <MultiMonthCalendarView
                            reservations={filteredReservations}
                            houses={houses}
                            onSelectReservation={handleSelectReservation}
                            onSelectSlot={canEdit ? handleSelectSlot : undefined}
                            clearSelection={showDetailModal}
                        />
                    )}
                    {viewMode === 'calendar' && (
                        <CalendarView
                            reservations={filteredReservations}
                            houses={houses}
                            onSelectReservation={handleSelectReservation}
                            onSelectSlot={canEdit ? handleSelectSlot : undefined}
                            clearSelection={showDetailModal}
                        />
                    )}

                    {viewMode === 'table' && renderTableView()}

                    {viewMode === 'list' && (
                        <ListView
                            reservations={filteredReservations}
                            houses={houses}
                            onSelectReservation={handleSelectReservation}
                        />
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Reservation Detail Modal */}
            <ReservationDetailModal
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                reservation={selectedReservation}
                houseName={selectedReservation ? getHouseName(selectedReservation.house_id) : undefined}
                onEdit={handleEditFromDetail}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                deleting={deletingReservation}
            />

            {/* Create/Edit Reservation Modal */}
            <CreateReservationModal
                show={showCreateModal}
                onHide={() => {
                    setShowCreateModal(false);
                    setSelectedReservation(null); // Clear selection when closing
                    setPrefilledDates(undefined); // Clear prefilled dates
                    setExtractedData(null); // Clear extracted data
                }}
                houses={houses}
                prefilledDates={prefilledDates}
                reservation={selectedReservation}
                extractedData={extractedData}
                onSubmit={handleCreateReservation}
            />
        </div>
    );
};

export default Reservations;